import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import ReactHtmlParser, { convertNodeToElement } from 'react-html-parser';

/**
 * Rotating news / referral banner for the new talent dashboard.
 * Functional / Tailwind port of WrippleEffectPanel: shows the talent dashboard
 * feed items (managed in Nova) sorted by display_index, rotating every 4s.
 * Manually changing slides stops auto-rotation (same as WrippleEffectPanel.setFeed).
 */

const ROTATE_INTERVAL_MS = 4000;

// Trix wraps content in <div>s — render as paragraphs, open links in a new tab
const transform = (node, index) => {
    if (node.name === 'div') {
        node.name = 'p';
        return convertNodeToElement(node, index, transform);
    }
    if (node.name === 'a') {
        node.attribs.target = '_blank';
        node.attribs.rel = 'noopener noreferrer';
        return convertNodeToElement(node, index, transform);
    }
    return undefined;
};

/**
 * If the body ends with a link (e.g. "...Refer them to us. <a>Learn more</a>"),
 * pull it out so it can be shown as the call-to-action on the right.
 * Links in the middle of the copy are left inline.
 */
const splitTrailingLink = (html = '') => {
    if (typeof DOMParser === 'undefined' || !html) return { html, cta: null };

    try {
        const root = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html').body.firstChild;
        const links = root.querySelectorAll('a[href]');
        const lastLink = links[links.length - 1];
        if (!lastLink) return { html, cta: null };

        // Anything meaningful after the link? Then it's part of a sentence — keep it inline.
        const range = document.createRange();
        range.setStartAfter(lastLink);
        range.setEnd(root, root.childNodes.length);
        if (range.toString().trim().length) return { html, cta: null };

        const cta = { href: lastLink.getAttribute('href'), label: lastLink.textContent.trim() || 'Learn more' };
        lastLink.remove();

        return { html: root.innerHTML, cta };
    } catch (error) {
        console.warn(error);
        return { html, cta: null };
    }
};

const FeedBanner = ({ items, className = '' }) => {
    const feedItems = useMemo(() => (
        TB.sortBy(items || [], 'display_index').map(item => ({ ...item, ...splitTrailingLink(item.body) }))
    ), [items]);

    const [currentIndex, setCurrentIndex] = useState(0);
    const [isAutoPlaying, setIsAutoPlaying] = useState(true);
    const [isPaused, setIsPaused] = useState(false);

    const total = feedItems.length;

    useEffect(() => {
        if (total < 2 || !isAutoPlaying || isPaused) return undefined;

        const interval = setInterval(() => {
            setCurrentIndex(index => (index + 1 >= total ? 0 : index + 1));
        }, ROTATE_INTERVAL_MS);

        return () => clearInterval(interval);
    }, [total, isAutoPlaying, isPaused]);

    if (!total) return null;

    const goTo = (step) => {
        setIsAutoPlaying(false);
        setCurrentIndex(index => (index + step + total) % total);
    };

    const arrowClasses = 'flex h-8 w-8 items-center justify-center rounded-full border-0 bg-transparent text-gray-400 hover:bg-gray-100 hover:text-primary';

    return (
        <section
            aria-roledescription="carousel"
            aria-label={`${laravel.platform_name} news`}
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
            onFocus={() => setIsPaused(true)}
            onBlur={() => setIsPaused(false)}
            className={`flex items-center gap-3 rounded-2xl border-0 border-l-4 border-solid border-primary-lighter bg-white px-4 py-4 shadow-[0_1px_3px_rgba(16,24,40,0.06),0_1px_2px_rgba(16,24,40,0.04)] sm:gap-4sm:px-6 ${className}`}
        >
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary" aria-hidden="true">
                <i className="fas fa-plus text-xs" />
            </span>

            {/* Slides are stacked in one grid cell so the banner keeps the height of the tallest item */}
            <div className="grid min-w-0 flex-1" aria-live={isAutoPlaying && !isPaused ? 'off' : 'polite'}>
                {feedItems.map((item, index) => {
                    const isActive = index === currentIndex;

                    return (
                        <div
                            key={`feed-item-${index}`}
                            aria-hidden={!isActive}
                            className={TB.mapClass('col-start-1 row-start-1 flex flex-col gap-x-6 gap-y-1 transition-all duration-500 sm:flex-row sm:items-center', {
                                'translate-y-0 opacity-100': isActive,
                                'pointer-events-none invisible translate-y-1 opacity-0': !isActive,
                            })}
                        >
                            <div className="min-w-0 flex-1 text-[15px] leading-relaxed text-gray-800 sm:text-base [&_a]:font-semibold [&_a]:text-primary [&_a]:underline [&_p]:m-0">
                                {ReactHtmlParser(item.html, { transform })}
                            </div>

                            {item.cta && (
                                <a
                                    href={item.cta.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    tabIndex={isActive ? 0 : -1}
                                    className="shrink-0 whitespace-nowrap text-sm font-semibold text-primary hover:text-primary-dark hover:no-underline"
                                >
                                    {item.cta.label} <span aria-hidden="true">→</span>
                                </a>
                            )}
                        </div>
                    );
                })}
            </div>

            {total > 1 && (
                <div className="flex shrink-0 items-center gap-0.5">
                    <button type="button" aria-label="Previous update" className={arrowClasses} onClick={() => goTo(-1)}>
                        <i className="fas fa-chevron-left text-xs" />
                    </button>
                    <button type="button" aria-label="Next update" className={arrowClasses} onClick={() => goTo(1)}>
                        <i className="fas fa-chevron-right text-xs" />
                    </button>
                </div>
            )}
        </section>
    );
};

FeedBanner.propTypes = {
    items: PropTypes.array,
    className: PropTypes.string,
};

FeedBanner.defaultProps = {
    items: typeof laravel !== 'undefined' ? laravel.talent_dashboard_feed_items : [],
};

export default FeedBanner;
